import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { selectUserIsAuthenticated, useQueryPopupOpen } from '@mwl/core-lib';
import { Button } from '@/components/Button/Button';
import { Loader } from '@/components/Loader/Loader';
import { emptyUrl } from '@/constants/casinoGame';
import { useTypedSelector } from '@/hooks';
import { useFullscreen } from '../../CasinoGamePage.context';
import type { GameSectionProps } from './GameSection.types';
import styles from './GameSection.module.scss';
const minHeightOfHeader = 60;
export const GameSection: FC<GameSectionProps> = ({
  gameId,
  isLoading,
  mode
}) => {
  const {
    t
  } = useTranslation('casino');
  const {
    isFullscreen
  } = useFullscreen();
  const {
    pathname
  } = useRouter();
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const {
    frameRef,
    url
  } = useFullscreen();
  const {
    createLinkProps
  } = useQueryPopupOpen();
  const [frameTop, setFrameTop] = useState<number | null>(null);
  const noAuthLink = useMemo(() => createLinkProps({
    queryName: 'register'
  }), [createLinkProps]);
  const linkProps = !isAuth ? noAuthLink : {
    href: {
      pathname,
      query: {
        gameId,
        mode: 'real'
      }
    }
  };
  const calculateFrameTop = useCallback(() => {
    if (frameRef.current) {
      const {
        top
      } = frameRef.current.getBoundingClientRect();
      setFrameTop(top >= 0 ? top : 0);
    }
  }, [frameRef]);
  useEffect(() => {
    const waitForRedraw = 0;
    let timeoutId: NodeJS.Timeout;
    const calculateFrameTopTimeout = () => {
      timeoutId = setTimeout(calculateFrameTop, waitForRedraw);
    };
    window.addEventListener('resize', calculateFrameTopTimeout);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateFrameTopTimeout);
    };
  }, [calculateFrameTop]);
  return <div className={styles.root} data-sentry-component="GameSection" data-sentry-source-file="GameSection.tsx">
      {isLoading && <Loader />}
      <div className={cn(styles.frame, {
      [styles.frameInvisible]: frameTop === null,
      [styles.frameFullHeight]: isFullscreen
    })} style={{
      [('--frame-top' as string)]: `${frameTop || minHeightOfHeader}px`
    }}>
        {url && url !== emptyUrl && <iframe onLoad={calculateFrameTop} className={styles.iframe} title={gameId} ref={frameRef} allowFullScreen allow="clipboard-write *" />}
      </div>

      {mode === 'DEMO' && <Button className={styles.linkButton} as="link" variant="filled" color="red" rounded fontSize="lg" cut={false} fontWeight="bold" {...linkProps}>
          <span className={styles.buttonText}>{t('play_for_money.play', 'play for real money')}</span>
        </Button>}
    </div>;
};